/**
 * Chain Constants - Centralized chain identifiers and metadata
 * Follow industry best practice: Single source of truth for all chain-related constants
 */

// Chain IDs - Use enum for type safety and autocomplete
export enum ChainId {
  ETHEREUM = 1,
  OPTIMISM = 10,
  BSC = 56,
  POLYGON = 137,
  BASE = 8453,
  ARBITRUM = 42161,
  AVALANCHE = 43114,
  LINEA = 59144,
  MODE = 34443,
  BLAST = 81457,
  SCROLL = 534352,
  ZKSYNC = 324,
  XLAYER = 196,
  MANTA = 169,
  ZIRCUIT = 48900,
  TAC = 239,
  UNICHAIN = 130,
  SONIC = 146,
  PLASMA = 9745,
  SEPOLIA = 11155111, // Testnet
}

// Chain Display Names
export const CHAIN_NAMES: Record<ChainId, string> = {
  [ChainId.ETHEREUM]: "Ethereum",
  [ChainId.OPTIMISM]: "Optimism",
  [ChainId.BSC]: "BNB Chain",
  [ChainId.POLYGON]: "Polygon",
  [ChainId.BASE]: "Base",
  [ChainId.ARBITRUM]: "Arbitrum",
  [ChainId.AVALANCHE]: "Avalanche",
  [ChainId.LINEA]: "Linea",
  [ChainId.MODE]: "Mode",
  [ChainId.BLAST]: "Blast",
  [ChainId.SCROLL]: "Scroll",
  [ChainId.ZKSYNC]: "zkSync Era",
  [ChainId.XLAYER]: "X Layer",
  [ChainId.MANTA]: "Manta Pacific",
  [ChainId.ZIRCUIT]: "Zircuit",
  [ChainId.TAC]: "TAC",
  [ChainId.UNICHAIN]: "Unichain",
  [ChainId.SONIC]: "Sonic",
  [ChainId.PLASMA]: "Plasma",
  [ChainId.SEPOLIA]: "Sepolia",
};

// Chain Icons - Centralized asset paths
export const CHAIN_ICONS: Record<ChainId, string> = {
  [ChainId.ETHEREUM]: "/assets/chains/ethereum.svg",
  [ChainId.OPTIMISM]: "/assets/chains/optimism.svg",
  [ChainId.BSC]: "/assets/chains/bsc.svg",
  [ChainId.POLYGON]: "/assets/chains/polygon.svg",
  [ChainId.BASE]: "/assets/chains/base.svg",
  [ChainId.ARBITRUM]: "/assets/bridge/arbitrum.svg",
  [ChainId.AVALANCHE]: "/assets/chains/avalanche.png",
  [ChainId.LINEA]: "/assets/chains/linea.svg",
  [ChainId.MODE]: "/assets/chains/mode.svg",
  [ChainId.BLAST]: "/assets/bridge/blast.svg",
  [ChainId.SCROLL]: "/assets/chains/scroll.svg",
  [ChainId.ZKSYNC]: "/assets/chains/zksync.svg",
  [ChainId.XLAYER]: "/assets/chains/xlayer.svg",
  [ChainId.MANTA]: "/assets/bridge/manta-pacific.svg",
  [ChainId.ZIRCUIT]: "/assets/chains/zircuit.svg",
  [ChainId.TAC]: "/assets/chains/tac.svg",
  [ChainId.UNICHAIN]: "/assets/chains/unichain.svg",
  [ChainId.SONIC]: "/assets/chains/sonic.png",
  [ChainId.PLASMA]: "/assets/chains/plasma.png",
  [ChainId.SEPOLIA]: "/assets/chains/ethereum.svg",
};

// Defi section mapping - which chain data to use for DeFi
export const CHAIN_DEFI_SECTION: Record<number, string> = {
  [ChainId.ETHEREUM]: "Ethereum",
  [ChainId.OPTIMISM]: "Optimism",
  [ChainId.SEPOLIA]: "Ethereum",
  [ChainId.XLAYER]: "Ethereum",
  [ChainId.ZKSYNC]: "zkSync",
  [ChainId.BASE]: "Base",
  [ChainId.ARBITRUM]: "Arbitrum",
  [ChainId.MODE]: "Mode",
  [ChainId.LINEA]: "Linea",
  [ChainId.BLAST]: "Blast",
  [ChainId.SCROLL]: "Scroll",
  [ChainId.AVALANCHE]: "Avalanche",
  [ChainId.SONIC]: "Sonic",
};

// Chains where minting has limits
export const MINT_LIMIT_CHAINS: ChainId[] = [
  ChainId.SCROLL,
  ChainId.ZKSYNC,
  ChainId.BASE,
  ChainId.OPTIMISM,
  ChainId.LINEA,
  ChainId.ZIRCUIT,
  ChainId.TAC,
  ChainId.SONIC,
  ChainId.PLASMA,
];

// Helper functions
export const getChainName = (chainId: number): string => {
  return CHAIN_NAMES[chainId as ChainId] || "Unknown Chain";
};

export const getChainIcon = (chainId: number): string => {
  return CHAIN_ICONS[chainId as ChainId] || "/assets/placeholder.svg";
};

export const isValidChain = (chainId: number): boolean => {
  return chainId in ChainId;
};
